import { QueryFunction } from '@tanstack/react-query';
import { BIG_ZERO } from 'app/constants';
import { IBaseSilo, Silo, SiloIncentivesController } from '@silo/typechain';
import { Web3Context } from '../web3Context';
import { DecoratedSilo, DecoratedSiloKey, SiloAsset } from './types';
import {
  adjustDepositApr,
  balanceOf,
  calculateRewardsApr,
  createSiloContract,
  createSiloIncentivesControllerContract,
  fetchIdentifiers,
  getCurrentBlockTimestamp,
} from '@silo/contract-utils';
import { convertCurrency, durationToSeconds } from '@silo/utils';
import { SiloConfiguration } from '@silo/config-legacy';
import {
  AssetKeyLegacy,
  BigIntWithDecimals,
  ChecksumAddress,
  LendingMerklRewardLegacy,
  NATIVE_CURRENCY_DECIMALS,
  assetKeysLegacy,
  getChecksumAddressOrThrow,
  makeAddressSync,
} from '@silo/types';
import { getCurveLpAssetAddress } from '../../features/util/curve-lp';
import { BigNumber } from 'ethers';
import { getRewardsTokenDecimals, getSiloIncentivesControllerAddress } from '../../features/silo/rewards/config';
import { fetchEmissionPerSecond, getRewardsPriceUsd } from '../../features/silo/rewards/utils';
import { promiseFetchQuotePrice } from '../price/fetchTokenPrice';
import { getConfigDepositMode } from '@silo/config';
import { pipe } from 'effect';
import { toLendingMerklRewardLegacy } from './to-lending-merkl-reward-legacy';
import { MerklCampaign } from '@silo/data';

export const queryDecoratedSilo: QueryFunction<DecoratedSilo, DecoratedSiloKey> = async ({
  queryKey: [, web3Context, siloAddress, siloConfig, merklCampaigns],
}) => {
  const { clientProvider, contracts, user, protocolInstance } = web3Context;
  const siloContract = createSiloContract({ siloAddress, provider: clientProvider });

  const { chainId } = web3Context;
  const quotePricePromise = promiseFetchQuotePrice({ chainId, contracts, provider: clientProvider });
  const rewardTokenPriceUsdPromise = quotePricePromise.then((quotePrice) =>
    getRewardsPriceUsd({
      protocolInstance,
      quotePrice,
      priceProvidersRepositoryContract: contracts.priceProvidersRepository,
    })
  );

  /**
   * TODO: rewards-apr
   *
   * Ideally, this value would be fetched from the Tower contract. But hard-coding for now.
   */
  // const { data: siloIncentivesControllerAddress } = useQueryCoordinates('SiloIncentivesController');
  const siloIncentivesControllerAddress = getSiloIncentivesControllerAddress(protocolInstance);
  const siloIncentivesController = createSiloIncentivesControllerContract({
    provider: clientProvider,
    address: siloIncentivesControllerAddress,
  });

  const [assets, userLTV, userMaximumLTV, userLiquidationThreshold, hasPosition, routerAddress] = await Promise.all([
    fetchAssets(
      siloAddress,
      siloContract,
      web3Context,
      siloConfig,
      siloIncentivesController,
      quotePricePromise,
      rewardTokenPriceUsdPromise,
      merklCampaigns
    ),
    user ? contracts.siloLens.getUserLTV(siloAddress, user).catch(() => BIG_ZERO) : BIG_ZERO,
    user ? contracts.siloLens.getUserMaximumLTV(siloAddress, user).catch(() => BIG_ZERO) : BIG_ZERO,
    user ? contracts.siloLens.getUserLiquidationThreshold(siloAddress, user).catch(() => BIG_ZERO) : BIG_ZERO,
    user ? contracts.siloLens.hasPosition(siloAddress, user) : false,
    web3Context.contracts.siloRepository.router(),
  ]);

  return {
    siloAddress,
    assets,
    userLTV,
    userMaximumLTV,
    userLiquidationThreshold,
    hasPosition,
    routerAddress: getChecksumAddressOrThrow(routerAddress),
  };
};

const fetchAssets = async (
  siloAddress: ChecksumAddress,
  siloContract: Silo,
  web3Context: Web3Context,
  siloConfig: SiloConfiguration,
  siloIncentivesController: SiloIncentivesController,
  quotePricePromise: Promise<BigIntWithDecimals>,
  rewardTokenPriceUsdPromise: Promise<BigIntWithDecimals>,
  merklCampaigns: MerklCampaign[]
): Promise<SiloAsset[]> => {
  const [{ assets, assetsStorage }, currentBlockTimestamp, quotePrice, rewardTokenPriceUsd] = await Promise.all([
    siloContract.getAssetsWithState(),
    getCurrentBlockTimestamp(web3Context.clientProvider),
    quotePricePromise,
    rewardTokenPriceUsdPromise,
  ]);

  const assetData = await Promise.all(
    assets.map((assetAddress, i) => {
      const assetMerklCampaigns = merklCampaigns.filter((campaign) => campaign.tokenAddress === assetAddress);

      return fetchAssetData({
        siloAddress,
        assetAddress: getChecksumAddressOrThrow(assetAddress),
        assetStorage: assetsStorage[i],
        web3Context,
        currentBlockTimestamp,
        assetKey: assetKeysLegacy[i],
        siloConfig,
        siloIncentivesController,
        quotePrice,
        rewardTokenPriceUsd,
        assetMerklCampaigns,
      });
    })
  );

  return assets.map((assetAddress, i) => {
    const isBaseAsset = i === 0;
    const nameOverride = siloConfig.name_MUST_SYNCHRONIZE;
    const symbolOverride = siloConfig.symbol_MUST_SYNCHRONIZE;
    const name = (isBaseAsset && nameOverride) || assetData[i].name;
    const symbol = (isBaseAsset && symbolOverride) || assetData[i].symbol;
    const { collateralOnlyDeposits } = assetsStorage[i];

    return {
      assetAddress: getChecksumAddressOrThrow(assetAddress),
      assetKey: assetKeysLegacy[i],
      collateralOnlyDeposits,
      ...assetData[i],
      name,
      symbol,
    };
  });
};

interface FetchAssetDataParams {
  siloAddress: ChecksumAddress;
  assetAddress: ChecksumAddress;
  assetStorage: IBaseSilo.AssetStorageStructOutput;
  web3Context: Web3Context;
  currentBlockTimestamp: number;
  assetKey: AssetKeyLegacy;
  siloConfig: SiloConfiguration;
  siloIncentivesController: SiloIncentivesController;
  quotePrice: BigIntWithDecimals;
  rewardTokenPriceUsd: BigIntWithDecimals;
  assetMerklCampaigns: MerklCampaign[];
}

const fetchAssetData = async ({
  siloAddress,
  assetAddress,
  assetStorage,
  web3Context: { contracts, user, clientProvider, chainId, protocolInstance },
  currentBlockTimestamp,
  assetKey,
  siloConfig,
  siloIncentivesController,
  quotePrice,
  rewardTokenPriceUsd,
  assetMerklCampaigns,
}: FetchAssetDataParams) => {
  const { isCurveLpAsset, userBalanceTokenAddress } = (() => {
    const curveLpAssetAddress = getCurveLpAssetAddress(siloConfig, assetKey);

    if (curveLpAssetAddress) {
      return {
        isCurveLpAsset: true,
        userBalanceTokenAddress: curveLpAssetAddress,
      };
    }

    return {
      isCurveLpAsset: false,
      userBalanceTokenAddress: assetAddress,
    };
  })();

  const totalDepositsPromise = contracts.siloLens.totalDepositsWithInterest(siloAddress, assetAddress);

  const marketAddress = makeAddressSync(siloAddress);
  const depositMode = getConfigDepositMode({ protocolKey: protocolInstance, marketAddress, assetKey });
  const isProtected = depositMode === 'protectedOnly' || depositMode === 'defaultFalse';

  /**
   * For some reason, combining these all into one Promise.all causes a couple
   * of TS issues. Specifically TS2493 and TS2769.
   */
  const siloData = Promise.all([
    fetchIdentifiers(assetAddress, clientProvider, chainId),
    contracts.siloLens.totalBorrowAmountWithInterest(siloAddress, assetAddress),
    contracts.siloRepository.isSiloPaused(siloAddress, assetAddress),
    contracts.siloLens.liquidity(siloAddress, assetAddress),
    contracts.siloLens.getUtilization(siloAddress, assetAddress),
    contracts.siloLens.depositAPY(siloAddress, assetAddress),
    contracts.siloLens.borrowAPY(siloAddress, assetAddress),
    contracts.priceProvidersRepository.getPrice(assetAddress).catch(() => BIG_ZERO),
    contracts.priceProvidersRepository.priceProviders(assetAddress),
    contracts.siloRepository.getMaximumLTV(siloAddress, assetAddress),
    contracts.siloRepository.getLiquidationThreshold(siloAddress, assetAddress),
    fetchEmissionPerSecond(siloIncentivesController, assetStorage.collateralToken),
    fetchEmissionPerSecond(siloIncentivesController, assetStorage.collateralOnlyToken),
    fetchEmissionPerSecond(siloIncentivesController, assetStorage.debtToken),
  ]);

  const projectedBlockTimestamp = currentBlockTimestamp + durationToSeconds({ minutes: 2 });

  const userData = Promise.all([
    user ? balanceOf(userBalanceTokenAddress, user, clientProvider, chainId) : BIG_ZERO,
    user
      ? totalDepositsPromise.then((totalDeposits) =>
          contracts.siloLens.balanceOfUnderlying(totalDeposits, assetStorage.collateralToken, user)
        )
      : BIG_ZERO,
    user
      ? /**
         * There is no lens method for grabbing the user's collateral only balance, so need to use balanceOfUnderlying
         */
        contracts.siloLens.balanceOfUnderlying(
          assetStorage.collateralOnlyDeposits,
          assetStorage.collateralOnlyToken,
          user
        )
      : BIG_ZERO,
    user ? contracts.siloLens.getBorrowAmount(siloAddress, assetAddress, user, currentBlockTimestamp) : BIG_ZERO,
    user ? contracts.siloLens.getBorrowAmount(siloAddress, assetAddress, user, projectedBlockTimestamp) : BIG_ZERO,
  ]);

  const totalDeposits = await totalDepositsPromise;

  const [
    identifiers,
    totalBorrowAmount,
    isPaused,
    liquidity,
    utilization,
    rawDepositAPY,
    borrowAPY,
    priceInNative,
    priceProviders,
    maximumLTV,
    liquidationThreshold,
    collateralEmissionPerSecond,
    protectedEmissionPerSecond,
    debtEmissionPerSecond,
  ] = await siloData;

  const [userBalance, collateralBalance, collateralOnlyBalance, debtBalance, projectedDebtBalance] = await userData;

  const depositAPY = BigNumber.from(adjustDepositApr(rawDepositAPY.toBigInt(), protocolInstance));
  const tokenPriceBi = {
    value: priceInNative.toBigInt(),
    decimals: NATIVE_CURRENCY_DECIMALS,
  };

  const collateralTotal = totalDeposits.toBigInt();
  const protectedTotal = assetStorage.collateralOnlyDeposits.toBigInt();
  const debtTotal = totalBorrowAmount.toBigInt();
  const { decimals, symbol } = identifiers;
  const tokenPriceUsd = convertCurrency(tokenPriceBi, quotePrice);
  const rewardTokenDecimals = getRewardsTokenDecimals(protocolInstance);

  const collateralRewardsApr = calculateRewardsApr({
    emissionPerSecond: collateralEmissionPerSecond,
    rewardTokenPriceUsd,
    rewardTokenDecimals,
    totalTokens: {
      value: collateralTotal,
      decimals,
    },
    tokenPriceUsd,
  });

  const protectedRewardsApr = calculateRewardsApr({
    emissionPerSecond: protectedEmissionPerSecond,
    rewardTokenPriceUsd,
    rewardTokenDecimals,
    totalTokens: {
      value: protectedTotal,
      decimals,
    },
    tokenPriceUsd,
  });

  const borrowRewardsApr = calculateRewardsApr({
    emissionPerSecond: debtEmissionPerSecond,
    rewardTokenPriceUsd,
    rewardTokenDecimals,
    totalTokens: {
      value: debtTotal,
      decimals,
    },
    tokenPriceUsd,
  });

  const lendingMerklRewards = assetMerklCampaigns.map(
    (campaign): LendingMerklRewardLegacy =>
      toLendingMerklRewardLegacy({
        campaign,
        totalCollateral: collateralTotal,
        totalProtected: protectedTotal,
        totalDebt: debtTotal,
        decimals,
        tokenPriceUsd,
        symbol,
      })
  );

  const depositRewardsApr = pipe(isProtected ? protectedRewardsApr : collateralRewardsApr, BigNumber.from);

  return {
    ...identifiers,
    totalDeposits,
    totalBorrowAmount,
    isPaused,
    liquidity,
    utilization,
    depositAPY,
    collateralRewardsApr: BigNumber.from(collateralRewardsApr),
    protectedRewardsApr: BigNumber.from(protectedRewardsApr),
    depositRewardsApr,
    borrowAPY,
    borrowRewardsApr: BigNumber.from(borrowRewardsApr),
    userBalanceTokenAddress: getChecksumAddressOrThrow(userBalanceTokenAddress),
    userBalance,
    collateralBalance,
    collateralOnlyBalance,
    debtBalance,
    projectedDebtBalance,
    priceInNative,
    priceProviders: getChecksumAddressOrThrow(priceProviders),
    maximumLTV,
    liquidationThreshold,
    isCurveLpAsset,
    lendingMerklRewards,

    collateralTotal,
    collateralEmissionPerSecond: collateralEmissionPerSecond,
    protectedTotal,
    protectedEmissionPerSecond,
    debtTotal,
    debtEmissionPerSecond,
    rewardTokenPriceUsd: rewardTokenPriceUsd.value,
    rewardTokenDecimals,
    tokenPriceUsd: tokenPriceUsd.value,
  };
};
